import { useEffect, useState } from 'react';
import { cn } from '../../../lib/utils';

// Sticky jump-nav generated from the section list. On desktop it's a left rail;
// on mobile a horizontal scroller. Doubles as a per-format checklist so no
// section is ever hidden. `groups` is an ordered array of { key, label }.
const SectionNav = ({ sections, groups }) => {
  const [active, setActive] = useState(sections[0]?.id);

  useEffect(() => {
    if (!sections.length) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: '-136px 0px -60% 0px', threshold: 0 }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  const jump = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!sections.length) return null;

  const grouped = groups
    .map((g) => ({ ...g, items: sections.filter((s) => s.group === g.key) }))
    .filter((g) => g.items.length);

  return (
    <nav aria-label="Sections" className="lg:sticky lg:top-32 lg:self-start">
      {/* Mobile: horizontal chips */}
      <div className="flex gap-2 overflow-x-auto pb-2 lg:hidden">
        {sections.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => jump(s.id)}
            className={cn(
              'shrink-0 rounded-sm border px-3 py-1.5 text-xs font-medium transition-colors',
              active === s.id
                ? 'border-primary/60 bg-primary/10 text-foreground'
                : 'border-border/60 text-muted-foreground hover:text-foreground'
            )}
          >
            {s.title}
          </button>
        ))}
      </div>

      {/* Desktop: grouped rail */}
      <div className="hidden lg:flex lg:flex-col lg:gap-5">
        {grouped.map((g) => (
          <div key={g.key} className="space-y-1.5">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
              {g.label}
            </div>
            {g.items.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => jump(s.id)}
                className={cn(
                  'block w-full rounded-sm border-l-2 px-3 py-1.5 text-left text-sm transition-colors',
                  active === s.id
                    ? 'border-l-primary bg-primary/5 font-medium text-foreground'
                    : 'border-l-transparent text-muted-foreground hover:border-l-border hover:text-foreground'
                )}
              >
                {s.title}
              </button>
            ))}
          </div>
        ))}
      </div>
    </nav>
  );
};

export default SectionNav;
